/**
 * Lead Score do cliente sobre DADOS REAIS (tela migrada do shell de fixture
 * `renderLeadScoreDetail`, Task 10).
 *
 * O score atual vem do `ConversationCore` (registro real) e as conversas que o
 * produziram são navegáveis. O **histórico** do score ainda não tem fonte:
 * `LEAD_SCORE_CHANGED` existe como tipo de evento em
 * `packages/event-contracts`, mas nenhum runtime o emite e `/api/admin/audit`
 * não expõe payload. Por isso o histórico aparece como fonte ausente — lista
 * vazia diria, falsamente, que o score nunca mudou (D-10.21).
 *
 * Redesign R6 (apresentação apenas): wrapper e header no padrão aprovado; cada
 * bloco `.lead-score__block` vira cartão tokenizado com h3 `text-lg
 * font-semibold text-ink`; o histórico vira lista tokenizada. Todos os `data-*`
 * (incl. `data-page`, `data-customer-id`, `data-field`, `data-record-id`), os
 * textos e os links são PRESERVADOS — as classes hook seguem no markup.
 *
 * Migrado para `.tsx`/JSX real (Cluster C — Atendimento & CRM, redesign por
 * perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * (`createElement` → JSX) + um ícone lucide por bloco (`SectionTitle`, mesmo
 * componente local de `PedidoDetalhe.tsx`), no mesmo padrão visual de
 * `KpiCard.tsx` (ícone em caixa colorida ao lado do título) — esta tela não
 * tinha nenhum ícone antes. Puramente decorativo — nenhum `data-*`/texto/rota
 * muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `AppShell.tsx`.
import React, { type ReactElement, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Gauge, History, MessageCircle, type LucideIcon } from "lucide-react";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { RecordTable } from "../components/RecordTable.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { buildLeadScoreDetail } from "../dashboard/customer.js";
import { formatChannel, formatCount, formatDateTime } from "../format.js";
import type { DashboardDataset } from "../dashboard/kpis.js";
import type { PlatformConversation } from "../api/types.js";

/**
 * Título de bloco com ícone em caixa colorida — mesmo padrão visual do
 * `kpi-card__icon` em `KpiCard.tsx`/`SectionTitle` de `PedidoDetalhe.tsx`
 * (ícone decorativo, o nome acessível do bloco continua sendo o texto do `h3`).
 */
function SectionTitle({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand-wash text-brand-text">
        <Icon className="h-4 w-4" aria-hidden focusable={false} />
      </span>
      <h3 className="text-lg font-extrabold text-ink">{children}</h3>
    </div>
  );
}

export interface LeadScoreViewProps {
  dataset: DashboardDataset;
  customerId: string;
}

export function LeadScoreView({ dataset, customerId }: LeadScoreViewProps): ReactElement {
  const view = buildLeadScoreDetail(dataset, customerId);
  if (!view) {
    return (
      <div className="page page--lead-score flex flex-col gap-6" data-page="lead-score">
        <h2 className="text-2xl font-black text-ink sm:text-3xl">Lead score</h2>
        <StatusPanel
          kind="empty"
          title="Cliente sem conversa conhecida"
          detail={`Sem conversa para ${customerId} não há score a explicar — o score é produzido pelo atendimento.`}
        />
      </div>
    );
  }
  const { detail, historyAvailable, conversations } = view;
  return (
    <div className="page page--lead-score flex flex-col gap-6" data-page="lead-score" data-customer-id={detail.customerId}>
      <header className="drilldown-header flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-2xl font-black text-ink sm:text-3xl">
          <Gauge className="h-5 w-5 shrink-0 text-brand-text" aria-hidden focusable={false} />
          {`Lead score — ${detail.customerName}`}
        </h2>
        <p className="drilldown-header__origin text-sm text-muted" data-field="currentScore">
          {`Score atual: ${formatCount(detail.currentScore)} de 100.`}
        </p>
        <Link to={`/clientes/${detail.customerId}`} className="drilldown-header__back text-sm text-brand-text hover:underline">
          Voltar ao cliente 360
        </Link>
      </header>
      <section
        className="lead-score__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
        aria-label="Conversas que produziram o score"
      >
        <SectionTitle icon={MessageCircle}>Conversas que produziram o score</SectionTitle>
        <RecordTable<PlatformConversation>
          caption="Conversas reais deste cliente, com o score registrado em cada uma"
          rows={conversations}
          idOf={(conversation) => conversation.id}
          emptyMessage="Nenhuma conversa registrada."
          columns={[
            {
              key: "id",
              label: "Conversa",
              render: (conversation) => <Link to={`/inbox/${conversation.id}`}>{conversation.id}</Link>
            },
            { key: "channel", label: "Canal", render: (conversation) => formatChannel(conversation.channel) },
            { key: "score", label: "Score na conversa", render: (conversation) => formatCount(conversation.leadScore) },
            { key: "messages", label: "Mensagens", render: (conversation) => formatCount(conversation.messages.length) },
            { key: "updatedAt", label: "Atualizada em", render: (conversation) => formatDateTime(conversation.updatedAt) }
          ]}
        />
      </section>
      <section
        className="lead-score__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
        aria-label="Histórico do score"
      >
        <SectionTitle icon={History}>Histórico do score</SectionTitle>
        {historyAvailable ? (
          <ol className="lead-score__history flex flex-col gap-2">
            {detail.history.map((event) => (
              <li
                key={event.id}
                data-record-id={event.id}
                className="rounded-control border border-border bg-surface-soft p-3 text-sm text-body"
              >
                {`${formatDateTime(event.occurredAt)}: ${formatCount(event.previousScore)} → ${formatCount(event.newScore)} (${event.reason})`}
              </li>
            ))}
          </ol>
        ) : (
          <StatusPanel
            kind="awaiting_source"
            title="Histórico do score ainda não publicado"
            detail="Nenhum runtime emite eventos LEAD_SCORE_CHANGED e /api/admin/audit não expõe payload de evento. Quando a fonte existir, o histórico aparece aqui sem alterar esta tela."
          />
        )}
      </section>
    </div>
  );
}

export function LeadScore(): ReactElement {
  const { customerId } = useParams<{ customerId: string }>();
  if (!customerId) {
    return <StatusPanel kind="empty" title="Informe o cliente na rota /clientes/:customerId/lead-score" />;
  }
  return (
    <DatasetBoundary>
      {(dataset: DashboardDataset) => <LeadScoreView dataset={dataset} customerId={customerId} />}
    </DatasetBoundary>
  );
}
